import React from "react";
import { useLocation, Navigate, Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { STATIC_PAGES } from "@/data/staticPages";
import { Mail, ChevronRight } from "lucide-react";
import { useSettings } from "@/lib/settings";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function StaticPage() {
  const location = useLocation();
  const slug = location.pathname.replace(/^\/+/, "");
  const page = STATIC_PAGES[slug];
  const { settings } = useSettings();
  if (!page) return <Navigate to="/" replace />;

  return (
    <div data-testid={`static-page-${slug}`}>
      <SEO
        title={page.seoTitle || page.title}
        description={page.description}
        canonical={`/${slug}`}
      />
      <div className="bg-gradient-to-br from-primary/5 via-background to-accent/5 py-14 border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
            <Link to="/" className="hover:text-primary">Ana Sayfa</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-foreground">{page.title}</span>
          </nav>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">{page.title}</h1>
          {page.hero && <p className="mt-4 text-lg text-muted-foreground max-w-2xl">{page.hero}</p>}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 space-y-10">
        {slug === "hakkimizda" && settings?.about_us_text ? (
          <section>
            <div 
              className="text-base leading-relaxed text-muted-foreground prose prose-slate max-w-none"
              dangerouslySetInnerHTML={{ __html: settings.about_us_text }}
            />
          </section>
        ) : slug === "iletisim" && settings?.contact_text ? (
          <section>
            <div 
              className="text-base leading-relaxed text-muted-foreground prose prose-slate max-w-none"
              dangerouslySetInnerHTML={{ __html: settings.contact_text }}
            />
          </section>
        ) : slug === "sss" && settings?.faq && settings.faq.length > 0 ? (
          <Accordion type="single" collapsible className="w-full">
            {settings.faq.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`}>
                <AccordionTrigger className="text-left font-display text-lg">{item.q}</AccordionTrigger>
                <AccordionContent className="text-base leading-relaxed text-muted-foreground whitespace-pre-wrap">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        ) : (
          page.sections.map((s, i) => {
            const content = s.p
              .replace(/{EMAIL}/g, settings?.email || "destek@sanostudent.com")
              .replace(/{KVKK_EMAIL}/g, settings?.email || "kvkk@sanostudent.com");
            return (
              <section key={i}>
                <h2 className="font-display text-2xl font-bold mb-3">{s.h}</h2>
                <p className="text-base leading-relaxed text-muted-foreground">{content}</p>
              </section>
            );
          })
        )}

        <div className="mt-16 p-6 rounded-xl border border-border bg-card flex items-center justify-between gap-4 flex-wrap">
          <div>
            <div className="font-display font-bold">Sorunuz mu var?</div>
            <div className="text-sm text-muted-foreground">Ekibimiz 1 iş günü içinde yanıtlar.</div>
          </div>
          <a href={`mailto:${page.contactEmail || settings?.email || "destek@sanostudent.com"}`} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium" data-testid="static-contact-cta">
            <Mail className="h-4 w-4" /> {page.contactEmail || settings?.email || "destek@sanostudent.com"}
          </a>
        </div>
      </div>
    </div>
  );
}
